import { useEffect, useRef, useState } from 'react'
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import Colors from '../utils/Colors'
import { CustomText } from './CustomText'

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`

const DialogBox = styled.div`
  background-color: #fff;
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 520px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 90vh;
`

const ScrollContent = styled.div`
  overflow-y: auto;
  flex: 1;
  padding-right: 4px;
`

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
`

const AcceptButton = styled.button`
  background-color: ${Colors.primaryGreen};
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 24px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    opacity: 0.88;
  }
`

const TENANT_8_CONSENT_TEXT =
  'Registracijom na platformu Etenim potvrđujem da sam upoznat/a s uvjetima korištenja i pravilima privatnosti platforme te dobrovoljno pristajem na obradu svojih osobnih podataka od strane poslodavca i platforme Etenim. Suglasan/na sam da se moje ime i prezime koriste u svrhu izrade i prikaza mog korisničkog profila na platformi Etenim, kao i za potrebe identifikacije unutar sustava. Također pristajem da se obrađuju podaci povezani s mojim aktivnostima na platformi, uključujući podatke o korištenju, prijavama, interakcijama, rezultatima aktivnosti i drugim informacijama vezanim uz korištenje platforme, isključivo u unaprijed određene svrhe. Potvrđujem da sam upoznat/a s time da se moji osobni podaci obrađuju sukladno važećim propisima o zaštiti osobnih podataka, uključujući Opću uredbu o zaštiti podataka (GDPR), te da u svakom trenutku imam pravo zatražiti pristup svojim podacima, njihov ispravak, brisanje ili ograničenje obrade, u skladu sa zakonom. Ova privola može biti povučena u bilo kojem trenutku slanjem maila na e-mail adresu info@etenim.app i marketing@gljz.hr. Povlačenjem privole korisnik gubi pravo pristupa svom korisničkom profilu na Etenim aplikaciji.'

interface TermsDialogProps {
  visible: boolean
  onAccept: () => void
  tenantId?: number | null
}

export const TermsDialog = ({ visible, onAccept, tenantId }: TermsDialogProps) => {
  const { t } = useTranslation()
  const [showTerms, setShowTerms] = useState(true)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (scrollRef.current) {
      disableBodyScroll(scrollRef.current)
    }
    return () => {
      clearAllBodyScrollLocks()
    }
  }, [visible, showTerms])

  if (!visible) return null

  if (showTerms) {
    return (
      <Overlay>
        <DialogBox style={{ maxWidth: 680 }}>
          <CustomText fontSize={20} fontWeight={700} color={Colors.darkGray}>
            {t('termsOfUsage')}
          </CustomText>
          <ScrollContent ref={scrollRef}>
            {tenantId === 8 && (
              <div style={{ marginBottom: 20 }}>
                <CustomText fontSize={14} fontWeight={700} color={Colors.darkGray} style={{ marginBottom: 8 }}>
                  Privola za obradu osobnih podataka i korištenje korisničkog profila
                </CustomText>
                <CustomText fontSize={14} lineHeight={1.6} color={Colors.darkGray}>
                  {TENANT_8_CONSENT_TEXT}
                </CustomText>
              </div>
            )}
            <CustomText fontSize={14} lineHeight={1.6} color={Colors.darkGray} style={{ whiteSpace: 'pre-line' }}>
              {t('termsText')}
            </CustomText>
          </ScrollContent>
          <ButtonRow>
            <AcceptButton
              onClick={() => {
                clearAllBodyScrollLocks()
                onAccept()
              }}
            >
              {t('acceptTermsOfService')}
            </AcceptButton>
          </ButtonRow>
        </DialogBox>
      </Overlay>
    )
  }

  return (
    <Overlay>
      <DialogBox>
        <CustomText fontSize={18} fontWeight={700} color={Colors.darkGray}>
          {t('termsOfUsage')}
        </CustomText>
        <CustomText fontSize={14} lineHeight={1.6} color={Colors.darkGray}>
          {t('toContinueAcceptTerms')}{' '}
          <span
            style={{ color: Colors.primaryGreen, textDecoration: 'underline', cursor: 'pointer' }}
            onClick={() => setShowTerms(true)}
          >
            {t('termsOfService')}
          </span>
          .
        </CustomText>
        <ButtonRow>
          <AcceptButton onClick={onAccept}>{t('accept')}</AcceptButton>
        </ButtonRow>
      </DialogBox>
    </Overlay>
  )
}
